import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  FulfillmentFlatTeamSettingCatalogDefinitions as originalDefinitions,
  type FulfillmentFlatTeamSettingKey as OriginalSettingKey,
} from "./catalog";
import {
  FulfillmentFlatTeamSettingGroupById,
  type FulfillmentFlatTeamSettingGroupId,
} from "./groups-shipping-new";

export type FulfillmentFlatTeamSettingKey =
  | "outboundLabels/suggestMerge/mergeConditions"
  | "outboundLabels/autoMerge/mergeConditions"
  | "outboundLabels/learnedPresets/parcel"
  | "outboundLabels/learnedPresets/weight"
  | "outboundLabels/batchSuggestions/statusOption"
  | "outboundLabels/batchSuggestions/minGroupCount"
  | "outboundLabels/batchSuggestions/groupingKey"
  | OriginalSettingKey
  | "outboundLabels/autoSplit/splitConditions"
  | (typeof orderManagementSettings)[number]["id"];

const autoSplitConditionsKey = "outboundLabels/autoSplit/splitConditions";
const autoSplitGroupId = "fulfillment.auto_split";

const orderManagementSettings = [
  {
    id: "outboundLabels/customs",
    groupId: "fulfillment.international_shipping",
    label: "Default customs information",
    searchLabels: [
      "Default content type",
      "Non-delivery option",
      "Customs declarations",
      "Predefined customs declarations",
      "Default contents description",
      "Sign customs as...",
      "AES exemption code",
      "USPS Exemption Code",
      "ITN usage",
    ],
    description:
      "Set customs declaration defaults, descriptions, signer, and export exemptions for international shipments.",
  },
  {
    id: "outboundLabels/documents",
    groupId: "fulfillment.printing",
    label: "Documents",
    description:
      "Manage the content and automatic behavior for labels, packing slips, pick lists, and Personalized Inserts.",
    searchLabels: [
      "Shipping label content",
      "Packing slip content and automation",
      "Personalized insert content",
      "Pick list content",
      "Local pickup labels",
      "Scan-to-verify printing",
      "One-click print composition",
      "Packing slip template",
      "Add marketplace tag on packing slip print",
      "Tag name",
      "Update marketplace order status when packing slip is printed",
      "Personalized insert template",
      "Print personalized insert when label is printed",
      "Order Number",
      "Secondary Order Name",
      "SKU",
      "Product Name",
      "Customer ID",
      "Order items by",
      "Font size",
      "Image size",
      "Split quantity by order",
      "Show all custom attributes",
      "Enable local pickup labels",
      "Auto-print personalized insert",
      "Auto print packing slip",
      "Workstation",
      "Delay by",
    ],
  },
  {
    id: "outboundLabels/defaultActionButton",
    groupId: "fulfillment.print_actions",
    label: "Default action button",
    description: "Choose the primary fulfillment action shown to operators.",
  },
  {
    id: "outboundLabels/defaultOneClickPrintBatchSize",
    groupId: "fulfillment.print_actions",
    label: "Default batch size for one-click print",
    description:
      "The default number of orders included when starting a one-click print batch.",
  },
  {
    id: "outboundLabels/address/autoCleanAddresses",
    groupId: "fulfillment.address",
    label: "Auto clean addresses",
    description:
      "Automatically standardize minor address details, such as street abbreviations.",
  },
  {
    id: "outboundLabels/address/enableAddressComparison",
    groupId: "fulfillment.address",
    label: "Reuse a customer’s verified address for close matches",
    description:
      "Clean up shipping addresses and hold orders that need correction.",
  },
  {
    id: "outboundLabels/address/autoPlaceHoldOnAddressVerificationFailure",
    groupId: "fulfillment.address",
    label: "Hold orders with unverified shipping addresses",
    description:
      "Clean up shipping addresses and hold orders that need correction.",
  },
  {
    id: "outboundLabels/address/autoPlaceHoldOnAddressVerificationFailureDurationDays",
    groupId: "fulfillment.address",
    label: "Hold duration",
    description:
      "Release the address-verification hold after this many days; use 0 for no automatic expiry.",
  },
  {
    id: "outboundLabels/address/addressOutreachAutomation",
    groupId: "fulfillment.customer_notifications",
    label: "Email customers to correct unverified shipping addresses",
    description:
      "Email customers about address corrections and pickup readiness.",
  },
  {
    id: "outboundLabels/address/addressVerificationReplyToEmail",
    groupId: "fulfillment.customer_notifications",
    label: "Reply-to email",
    description:
      "Replies go to this address, or your connected support email if left blank.",
  },
  {
    id: "outboundLabels/address/addressVerificationCcEmail",
    groupId: "fulfillment.customer_notifications",
    label: "CC email",
    description:
      "This address will be CC'd on all address verification emails sent to customers.",
  },
  {
    id: "outboundLabels/blockPrintingBeyondInventory",
    groupId: "fulfillment.safeguards",
    label: "Prevent label creation for out-of-stock items",
    description: "Applies only to inventory tracked by Redo.",
  },
  {
    id: "outboundLabels/pinChecks/enabled",
    groupId: "fulfillment.safeguards",
    label: "Require PIN approval for selected fulfillment actions",
    searchLabels: [
      "4-digit team PIN for fulfillment approvals",
      "Verify items without scanning",
      "Void labels",
      "Reship orders",
      "Reprint labels",
      "Reprint packing slips",
      "Continue scanning with a packing slip outdated by merging",
      "Merge shipments after a packing slip has been printed",
      "Override an item’s “Not received” status",
      "Cancel orders",
      "Mark as fulfilled",
      "Create purchase order",
      "Skip warehouse bin selection",
      "Bypass parcel photo requirement",
      "Purchase labels at or above a set price",
      "Label price threshold",
      "Purchase labels for packages with no weight entered",
      "Change shipping service",
    ],
    description:
      "Require approval to verify items without scanning, void labels, reship orders, reprint labels or packing slips, cancel orders, mark as fulfilled, create purchase orders, bypass warehouse bins or parcel photos, and change shipping services.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/enabled",
    groupId: "fulfillment.package_photos",
    label: "Enable package photos",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/minCount",
    groupId: "fulfillment.package_photos",
    label: "Minimum required photos",
    description:
      "Required before buying or printing a label. Set to 0 to make photos optional.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/maxCount",
    groupId: "fulfillment.package_photos",
    label: "Maximum photos per package",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/enabled",
    groupId: "fulfillment.package_photos",
    label: "Auto-capture a package photo after verification",
    description:
      "Uses the selected camera in Scan to Verify to capture a photo once the package is still.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/motionThreshold",
    groupId: "fulfillment.package_photos",
    label: "Motion sensitivity threshold",
    description: "Lower values require the package to be more still.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/countdownMs",
    groupId: "fulfillment.package_photos",
    label: "Capture countdown (ms)",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/armDelayMs",
    groupId: "fulfillment.package_photos",
    label: "Delay after verification (ms)",
    description: "Wait before checking for a still package.",
  },
  {
    id: "outboundLabels/weightFormat",
    groupId: "fulfillment.display",
    label: "Weight unit",
    description: "Display & units",
  },
  {
    id: "outboundLabels/degroupItemsByCustomAttributes",
    groupId: "fulfillment.display",
    label: "Show items separately when custom attributes differ",
    description: "Display & units",
  },
  {
    id: "outboundLabels/hideUnderscoreCustomAttributes",
    groupId: "fulfillment.display",
    label: 'Hide custom attributes starting with "_"',
    description: "Display & units",
  },
  {
    id: "outboundLabels/liveViewCountsEnabled",
    groupId: "fulfillment.display",
    label: "Show live unfulfilled order counts in the fulfillment sidebar",
    description: "Display & units",
  },
  {
    id: "outboundLabels/billOfMaterials/alwaysHideInputItems",
    groupId: "fulfillment.bundles",
    label:
      "Show bundles instead of components on packing slips, pick lists, and Scan to Verify",
    description: "Inventory still deducts the component items.",
  },
  {
    id: "outboundLabels/billOfMaterials/alwaysExpandBomIgnoreInventory",
    groupId: "fulfillment.bundles",
    label:
      "Include components for every bundle, even when assembled stock is available",
    description:
      "Pick lists, packing slips, and Scan to Verify include components for all ordered bundles, not just those without assembled stock. Your bundle display settings still apply.",
  },
  {
    id: "outboundLabels/billOfMaterials/showBomInputsInGroupTable",
    groupId: "fulfillment.bundles",
    label: "Show bundle components in the fulfillment table",
    description: "Bundles",
  },
  {
    id: "outboundLabels/preferredReturnLabelCarrier",
    groupId: "fulfillment.return_label_defaults",
    label: "Preferred carrier",
    description:
      "With no preference, Redo tries the original shipment's carrier, then the cheapest eligible rate.",
  },
  {
    id: "outboundLabels/preferredReturnLabelService",
    groupId: "fulfillment.return_label_defaults",
    label: "Preferred service",
    description: "For return labels created during outbound fulfillment.",
  },
  {
    id: "outboundLabels/sendPickupEmailOnOverride",
    groupId: "fulfillment.customer_notifications",
    label: "Email customers when converted pickup orders are ready",
    description:
      "Notify customers when a shipping order converted to pickup is ready.",
  },
  {
    id: "outboundLabels/customMetadata",
    groupId: "fulfillment.custom_field_mappings",
    label: "Custom field mappings",
    searchLabels: [
      "Order metafields",
      "Custom attributes",
      "Product metafields",
      "Variant metafields",
      "Where is the field stored?",
      "What is this field associated with?",
      "What kind of order field?",
      "Namespace",
      "Value type",
      "Name in Redo (optional)",
      "Use for scanning",
    ],
    description:
      "Choose order metafields, custom attributes, product metafields, and variant metafields for fulfillment columns and CSV exports.",
  },
  {
    id: "outboundLabels/variantReceivedNamespace",
    groupId: "fulfillment.custom_field_mappings",
    label: "Received metafield namespace",
    description:
      "The namespace of the Shopify variant metafield that tracks whether an item has been received.",
  },
  {
    id: "outboundLabels/variantReceivedKey",
    groupId: "fulfillment.custom_field_mappings",
    label: "Received metafield key",
    searchLabels: ["Open - Not Received", "Not received status"],
    description:
      "Orders with a variant where this metafield is false stay Open - Not Received. Leave blank to treat every item as received.",
  },
  {
    id: "outboundLabels/suggestMerge/enabled",
    groupId: "fulfillment.merge_suggestions",
    label: "Suggest merging fulfillments that meet all the selected criteria",
    description:
      "Turn on merge suggestions for fulfillments that meet all the selected criteria.",
  },
  {
    id: "outboundLabels/autoMerge/enabled",
    groupId: "fulfillment.auto_merge",
    label:
      "Automatically merge fulfillments that meet all the selected criteria",
    description:
      "Automatically merge fulfillments that meet all the selected criteria",
  },
  {
    id: "outboundLabels/autoMerge/refundOtherShippingOptions",
    groupId: "fulfillment.merge_outcomes",
    label: "Refund other shipping charges",
    description:
      "Keep the selected shipping service's charge and refund the rest, where supported.",
  },
  {
    id: "outboundLabels/autoMerge/addTags/enabled",
    groupId: "fulfillment.merge_outcomes",
    label: "Add marketplace order tags to auto-merged orders",
    description:
      "Add the selected marketplace order tags after an automatic merge.",
  },
  {
    id: "outboundLabels/autoMerge/addNote/enabled",
    groupId: "fulfillment.merge_outcomes",
    label: "Add a marketplace note to auto-merged orders (Shopify only)",
    description: "Add a marketplace note to auto-merged orders (Shopify only)",
  },
  {
    id: "outboundLabels/autoSplit/enabled",
    groupId: "fulfillment.auto_split",
    label: "Split items matching the selected criteria into separate shipments",
    description:
      "Split items matching the selected criteria into separate shipments",
  },
  {
    id: "outboundLabels/autoSplit/splitIntoIndividualShipments",
    groupId: "fulfillment.split_outcomes",
    label: "Split into individual shipments",
    description:
      "Create a separate shipment for each matching unit, including multiple units of the same item.",
  },
  {
    id: "outboundLabels/autoSplit/leaveOne",
    groupId: "fulfillment.split_outcomes",
    label: "Keep one unit in the original shipment",
    description:
      "Keep one matching unit in the original shipment when its _leave_one attribute is true.",
  },
  {
    id: "outboundLabels/autoSplit/addTags/enabled",
    groupId: "fulfillment.split_outcomes",
    label: "Add Marketplace order tag after splitting",
    description: "Add Marketplace order tag after splitting",
  },
  {
    id: "outboundLabels/autoMerge/serviceLevelPriority",
    groupId: "fulfillment.merge_outcomes",
    label: "Shipping service to keep after merging",
    description:
      "Choose which original order's shipping service to keep after merging.",
  },
  {
    id: "outboundLabels/autoMerge/exceptionConditions",
    groupId: "fulfillment.merge_exceptions",
    label: "Auto-merge exceptions",
    searchLabels: [
      "Marketplace order tag is any of",
      "Pick list printed",
      "Order is older than",
      "Sales channel is any of",
      "Product name includes any of",
      "Marketplace product tag is any of",
      "Redo shipment tag is any of",
      "SKU is any of",
      "Shipment has missing bins",
      "Shipment has missing warehouse bins",
      "Number of orders is at least",
      "Merged item quantity would exceed",
    ],
    description:
      "Prevent merging shipments that match any of the selected criteria: Marketplace order tag is any of, Pick list printed, Order is older than, Sales channel is any of, Product name includes any of, Marketplace product tag is any of, Redo shipment tag is any of, SKU is any of, Shipment has missing bins, Shipment has missing warehouse bins, Number of orders is at least, Merged item quantity would exceed.",
  },
  {
    id: "outboundLabels/autoSplit/productMappings",
    groupId: "fulfillment.split_sku_mappings",
    label: "Product SKU mappings",
    searchLabels: ["Trigger SKU", "Companion SKUs"],
    description:
      "Keep Companion SKUs with a Trigger SKU when it moves to a separate shipment.",
  },
  {
    id: "outboundLabels/autoSplit/exceptionConditions/orderTags",
    groupId: "fulfillment.split_exceptions",
    label: "Split exceptions",
    searchLabels: ["Marketplace order tag is any of"],
    description:
      "Prevent splitting orders when Marketplace order tag is any of the selected tags.",
  },
] as const;

function updateExistingDefinition(
  definition: (typeof originalDefinitions)[number],
) {
  if (definition.id === "outboundLabels/defaultShipFromLocationExternalId") {
    return {
      ...definition,
      metadata: { ...definition.metadata, label: "Ship from locations" },
    };
  }
  if (definition.id === "outboundLabels/address/internationalAddressOutreach") {
    return {
      ...definition,
      metadata: {
        ...definition.metadata,
        label: "Apply address outreach and holds outside the US",
        description:
          "Applies enabled address holds and correction emails to non-US addresses. Set up emails under Customer notifications.",
        changeGuidance: {
          summary:
            "Turn on email outreach or address holds before editing this setting. When both are off, the toggle is disabled and its saved selection is preserved.",
        },
      },
    };
  }
  return definition;
}

export const FulfillmentFlatTeamSettingCatalogDefinitions = [
  ...orderManagementSettings.map(({ id, groupId, ...setting }) => ({
    id,
    groupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting,
      group: FulfillmentFlatTeamSettingGroupById[groupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings" as const, key: id },
  })),
  {
    id: "outboundLabels/suggestMerge/mergeConditions",
    groupId: "fulfillment.merge_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Merge suggestions",
        searchLabels: [
          "Ship-from addresses match",
          "Delivery addresses match",
          "Delivery methods match",
          "Orders belong to the same customer record",
          "Customer names match",
          "Packing slips have not been printed",
          "Payment information matches",
          "Billing addresses match",
          "Recipient names match",
          "Order IDs do not match",
          "Fulfillment status",
        ],
        description:
          "Choose matching requirements for merge suggestions, including addresses, customer details, payment information, packing-slip status, delivery method, different order IDs, and shipment status. Ship-from matching is always required. Criteria: Ship-from addresses match, Delivery addresses match, Delivery methods match, Orders belong to the same customer record, Customer names match, Packing slips have not been printed, Payment information matches, Billing addresses match, Recipient names match, Order IDs do not match, Fulfillment status.",
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.merge_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/suggestMerge/mergeConditions",
    },
  },
  {
    id: "outboundLabels/autoMerge/mergeConditions",
    groupId: "fulfillment.auto_merge",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Auto-merge",
        searchLabels: [
          "Ship-from addresses match",
          "Delivery addresses match",
          "Delivery methods match",
          "Orders belong to the same customer record",
          "Customer names match",
          "Packing slips have not been printed",
          "Payment information matches",
          "Billing addresses match",
          "Recipient names match",
          "Order IDs do not match",
          "Fulfillment status",
        ],
        description:
          "Choose matching requirements for automatic shipment merging. Delivery address, ship-from address, and delivery method must match. Auto-merge allows only open shipments, or open and payment pending shipments. Exceptions and post-merge actions are configured separately. Criteria: Ship-from addresses match, Delivery addresses match, Delivery methods match, Orders belong to the same customer record, Customer names match, Packing slips have not been printed, Payment information matches, Billing addresses match, Recipient names match, Order IDs do not match, Fulfillment status.",
      },
      group: FulfillmentFlatTeamSettingGroupById["fulfillment.auto_merge"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/autoMerge/mergeConditions",
    },
  },
  {
    id: "outboundLabels/learnedPresets/parcel",
    groupId: "fulfillment.remember_shipment_choices",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Remember package choice",
        description:
          "Reuse the package selected for shipments with matching contents.",
        synonyms: ["remember package choice"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById[
          "fulfillment.remember_shipment_choices"
        ],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/learnedPresets/parcel",
    },
  },
  {
    id: "outboundLabels/learnedPresets/weight",
    groupId: "fulfillment.remember_shipment_choices",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Remember package weight",
        description:
          "Reuse the weight entered for shipments with matching contents.",
        synonyms: ["remember package weight"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById[
          "fulfillment.remember_shipment_choices"
        ],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/learnedPresets/weight",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/statusOption",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Fulfillment status",
        description:
          "Choose which shipment statuses qualify for batch suggestions.",
        synonyms: ["batch shipment statuses"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/statusOption",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/minGroupCount",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Minimum number of matching shipments",
        description:
          "Require this many matching shipments before suggesting a batch.",
        synonyms: ["minimum batch shipments"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/minGroupCount",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/groupingKey",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Group suggested items in bulk shipments by",
        description:
          "Match items by variant ID or SKU when suggesting shipment batches.",
        synonyms: ["batch item matching"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/groupingKey",
    },
  },
  ...originalDefinitions.map(updateExistingDefinition),
  {
    id: autoSplitConditionsKey,
    groupId: autoSplitGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Auto-split conditions",
        searchLabels: [
          "SKU",
          "Product name",
          "Marketplace product tags",
          "Receiving status",
          "Line item custom attribute",
        ],
        description:
          "Split matching items into separate shipments when auto-split is enabled. Saved conditions are preserved when auto-split is off.",
        synonyms: [
          "auto-split conditions",
          "auto split criteria",
          "split order conditions",
          "product split rules",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[autoSplitGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: autoSplitConditionsKey },
  },
] satisfies readonly SettingDefinition<
  FulfillmentFlatTeamSettingKey,
  FulfillmentFlatTeamSettingGroupId,
  TeamSettingsSettingValueSource<FulfillmentFlatTeamSettingKey>
>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentFlatTeamSettingMetadataByKey = Object.fromEntries(
  FulfillmentFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<FulfillmentFlatTeamSettingKey, SettingMetadata>;
